import type {
  JourneyMessage,
  LegacyJourneyContent,
} from "@hightouch/lib/llm/schema";
import type {
  JourneyGraphEnvelope,
  JourneyMessageEnvelope,
} from "@hightouch/lib/journeys/envelope";
import { produce } from "immer";
import { v5 as uuidv5 } from "uuid";

import { hydrateConditionWithIdsMutative } from "src/components/audiences/utils";
import {
  isScheduleConfig,
  type Schedule,
  ScheduleIntervalUnit,
  ScheduleType,
} from "src/components/schedule/types";
import type { JourneyQuery } from "src/graphql";
import {
  MESSAGE_NODE_HEIGHT,
  NODE_SPACING,
  NODE_WIDTH,
} from "src/pages/journeys/constants";
import type { JourneyContextConfig } from "src/pages/journeys/types";
import { hydrateNodeConfigConditionsMutative } from "src/pages/journeys/utils/hydrate-node-conditions";
import { getAdditionalHeightFromNotes } from "src/pages/journeys/utils/layout";
import {
  type JourneyExitCriteriaConfig,
  JourneyStatus,
} from "src/types/journeys";
import { ConditionType, type RootCondition } from "src/types/visual";

type JourneyOutputContent = JourneyMessage["content"];
type EnvelopeJourney = JourneyGraphEnvelope["journey"];
type EnvelopeNode = JourneyGraphEnvelope["nodes"][number];
type PersistedJourney = JourneyQuery["journeys"][number];
type PersistedJourneyNode = PersistedJourney["nodes"][number];
type PersistedJourneyNodeState = Pick<
  PersistedJourneyNode,
  | "campaign_messages"
  | "decision_engine_flow_links"
  | "experiment"
  | "id"
  | "number_unique_users"
  | "number_users"
>;
type PersistedJourneyState = Pick<
  PersistedJourney,
  "execution_mode" | "reset" | "runs_aggregate" | "schedule"
> & {
  nodes: readonly PersistedJourneyNodeState[];
  status: JourneyStatus;
};

type AdaptedEnvelopeBuilderJourney = Omit<
  LegacyJourneyContent,
  "description" | "exit_criteria" | "schedule" | "status"
> & {
  id?: string;
  description: string | null;
  schedule?: Schedule;
  exit_criteria: JourneyExitCriteriaConfig | null;
  context_config: JourneyContextConfig | null;
  execution_mode?: PersistedJourney["execution_mode"];
  reset?: PersistedJourney["reset"];
  runs_aggregate?: PersistedJourney["runs_aggregate"];
  status: JourneyStatus;
};

/**
 * Legacy content carries no id of its own. Once saved, the id lives in the
 * message's `newId`, and the builder reads it from `id` to tell an existing
 * journey from a new one.
 */
type AdaptedLegacyBuilderJourney = LegacyJourneyContent & { id?: string };

export type JourneyOutputBuilderJourney =
  | AdaptedLegacyBuilderJourney
  | AdaptedEnvelopeBuilderJourney;

const NEW_NODE_GRID_COLUMNS = 3;

const createConditionIdFactory = (scope: string) => {
  let index = 0;
  return () => uuidv5(`${scope}:${index++}`, uuidv5.URL);
};

const INTERVAL_UNIT_TO_BUILDER_UNIT: Record<
  "minute" | "hour" | "day" | "week",
  ScheduleIntervalUnit
> = {
  minute: ScheduleIntervalUnit.MINUTE,
  hour: ScheduleIntervalUnit.HOUR,
  day: ScheduleIntervalUnit.DAY,
  week: ScheduleIntervalUnit.WEEK,
};

const adaptSchedule = (
  schedule: EnvelopeJourney["schedule"],
): Schedule | undefined => {
  if (schedule === undefined) {
    return undefined;
  }

  switch (schedule.type) {
    case "interval":
      return {
        ...schedule,
        type: ScheduleType.INTERVAL,
        schedule: {
          interval: {
            ...schedule.schedule.interval,
            unit: INTERVAL_UNIT_TO_BUILDER_UNIT[
              schedule.schedule.interval.unit
            ],
          },
        },
      };
    case "cron":
      return { ...schedule, type: ScheduleType.CRON };
    case "visual_cron":
      return { ...schedule, type: ScheduleType.CUSTOM };
    default:
      throw new Error("Unsupported journey schedule type");
  }
};

const adaptExitCriteria = (
  exitCriteria: EnvelopeJourney["exit_criteria"],
): JourneyExitCriteriaConfig | null => {
  if (exitCriteria === null) {
    return null;
  }

  return produce(
    {
      ...exitCriteria,
      type: exitCriteria.type === "and" ? ConditionType.And : ConditionType.Or,
      conditions: exitCriteria.conditions as unknown as RootCondition[],
    } satisfies JourneyExitCriteriaConfig,
    (draft) =>
      hydrateConditionWithIdsMutative(
        draft,
        false,
        createConditionIdFactory("journey-exit-criteria"),
      ),
  );
};

type PositionableNode = Pick<EnvelopeNode, "id" | "position">;

const hasPosition = <T extends PositionableNode>(
  node: T,
): node is T & { position: { x: number; y: number } } =>
  node.position != null &&
  Number.isFinite(node.position.x) &&
  Number.isFinite(node.position.y);

const placeNewNodes = <T extends PositionableNode>(
  nodes: T[],
  notes: JourneyGraphEnvelope["journey"]["notes"],
): T[] => {
  const positionedNodes = nodes.filter(hasPosition);
  if (positionedNodes.length === 0) {
    return nodes;
  }

  const gridStartX =
    Math.max(...positionedNodes.map((node) => node.position.x)) +
    NODE_WIDTH +
    NODE_SPACING;
  const gridStartY = Math.min(
    ...positionedNodes.map((node) => node.position.y),
  );
  const gridColumnWidth = NODE_WIDTH + NODE_SPACING;
  const newNodes = nodes.filter((node) => !hasPosition(node));
  const gridRowHeights = Array.from(
    { length: Math.ceil(newNodes.length / NEW_NODE_GRID_COLUMNS) },
    (_, rowIndex) =>
      Math.max(
        ...newNodes
          .slice(
            rowIndex * NEW_NODE_GRID_COLUMNS,
            (rowIndex + 1) * NEW_NODE_GRID_COLUMNS,
          )
          .map(
            (node) =>
              MESSAGE_NODE_HEIGHT +
              getAdditionalHeightFromNotes(node.id, notes),
          ),
      ),
  );
  const gridRowOffsets: number[] = [];
  let nextRowOffset = 0;
  gridRowHeights.forEach((rowHeight) => {
    gridRowOffsets.push(nextRowOffset);
    nextRowOffset += rowHeight + NODE_SPACING;
  });
  let gridIndex = 0;

  return nodes.map((node) => {
    if (hasPosition(node)) {
      return node;
    }

    const rowIndex = Math.floor(gridIndex / NEW_NODE_GRID_COLUMNS);
    const position = {
      x: gridStartX + (gridIndex % NEW_NODE_GRID_COLUMNS) * gridColumnWidth,
      y: gridStartY + (gridRowOffsets[rowIndex] ?? 0),
    };
    gridIndex += 1;

    return { ...node, position };
  });
};

const adaptSyncConfig = (
  syncConfig: NonNullable<EnvelopeNode["sync_configs"]>[number],
) => {
  if ("placeholder_destination_id" in syncConfig) {
    return {
      ...syncConfig,
      placeholder_destination_id: Number(syncConfig.placeholder_destination_id),
      destination_instance_id: null,
      mode: "placeholder-mode" as const,
      exit_config: {},
    };
  }

  return {
    ...syncConfig,
    destination_instance_id: Number(syncConfig.destination_instance_id),
    exit_config: {
      remove_after: syncConfig.exit_config.remove_after ?? null,
      remove_on_journey_exit:
        syncConfig.exit_config.remove_on_journey_exit ?? null,
      expire_after: syncConfig.exit_config.expire_after ?? null,
      wait_for: syncConfig.exit_config.wait_for ?? null,
      on_timeout: syncConfig.exit_config.on_timeout ?? null,
      send_in_run: syncConfig.exit_config.send_in_run ?? null,
    },
    placeholder_destination_id: null,
    full_sync_config: null,
  };
};

export const isJourneyGraphEnvelopeContent = (
  content: JourneyOutputContent,
): content is JourneyMessageEnvelope => "journey" in content;

export const hasJourneyId = (
  journey: object,
): journey is object & { id: string } =>
  "id" in journey && typeof journey.id === "string" && journey.id.length > 0;

export const getPersistedJourneyId = (
  content: JourneyOutputContent,
): string | undefined =>
  content.newId ??
  (isJourneyGraphEnvelopeContent(content) ? content.journey.id : undefined);

/** Adapts persisted journey output into the database-row shape read by the builder. */
export const journeyOutputToBuilderJourney = (
  content: JourneyOutputContent,
  options?: {
    persistedJourney?: PersistedJourneyState;
  },
): JourneyOutputBuilderJourney => {
  const persistedJourneyId = getPersistedJourneyId(content);

  if (!isJourneyGraphEnvelopeContent(content)) {
    // Legacy content has no `journey` wrapper and no id of its own, so the
    // saved id has to be surfaced here so the builder knows which journey to load.
    return persistedJourneyId
      ? { ...content, id: persistedJourneyId }
      : content;
  }

  const { notes, ...journey } = content.journey;
  const persistedJourney = persistedJourneyId
    ? options?.persistedJourney
    : undefined;
  const persistedNodesById = new Map(
    persistedJourney?.nodes.map((node) => [node.id, node]),
  );
  const nodes = content.nodes.map(
    ({ message_config, sync_configs, ...node }) => {
      const persistedNode = persistedNodesById.get(node.id);
      const campaignMessages = message_config
        ? (persistedNode?.campaign_messages ?? []).filter(
            (campaignMessage) =>
              campaignMessage.message_id === message_config.message_id,
          )
        : [];
      const config = produce(
        {
          ...node.config,
          message_config: message_config ?? null,
        },
        (draft) =>
          hydrateNodeConfigConditionsMutative(
            draft,
            createConditionIdFactory(`journey-node:${node.id}`),
          ),
      );

      return {
        ...node,
        description: node.description ?? null,
        segment_id: node.segment_id ?? null,
        event_relationship_id: node.event_relationship_id ?? null,
        number_users: persistedNode?.number_users ?? null,
        number_unique_users: persistedNode?.number_unique_users ?? null,
        experiment: persistedNode?.experiment ?? null,
        campaign_messages: campaignMessages,
        // The envelope has no decision outcome or context columns; the canvas rebuilds both from the saved flow links.
        decision_engine_flow_links:
          persistedNode?.decision_engine_flow_links ?? [],
        sync_configs: sync_configs?.map(adaptSyncConfig) ?? [],
        config,
      };
    },
  );
  const positionedNodes = placeNewNodes(nodes, notes);
  // The envelope has no execution mode and its schedule is optional, so an edit
  // to a saved journey keeps the saved values the agent did not set.
  const persistedSchedule = isScheduleConfig(persistedJourney?.schedule)
    ? persistedJourney.schedule
    : undefined;

  return {
    ...journey,
    ...(persistedJourneyId ? { id: persistedJourneyId } : {}),
    ...(persistedJourney && {
      execution_mode: persistedJourney.execution_mode,
      reset: persistedJourney.reset,
      runs_aggregate: persistedJourney.runs_aggregate,
    }),
    status: persistedJourney?.status ?? JourneyStatus.Disabled,
    parent_model_id: Number(journey.parent_model_id),
    schedule: adaptSchedule(journey.schedule) ?? persistedSchedule,
    exit_criteria: adaptExitCriteria(journey.exit_criteria),
    nodes: positionedNodes,
    edges: content.edges,
    notes,
  };
};

/** The builder journey an applied agent edit opens on the canvas of a saved journey. */
export const agentEditToBuilderJourney = (
  content: JourneyOutputContent,
  persistedJourney: PersistedJourneyState,
): JourneyOutputBuilderJourney => {
  const adaptedJourney = journeyOutputToBuilderJourney(content, {
    persistedJourney,
  });
  const savedNodeIds = new Set(persistedJourney.nodes.map((node) => node.id));
  return {
    ...adaptedJourney,
    // A tile without a position makes the canvas lay out the whole graph, so added tiles land between their neighbors.
    // Checks the saved journey, not the edit's changes, so reapplying a saved edit keeps its saved positions.
    nodes: adaptedJourney.nodes.map((node) =>
      savedNodeIds.has(node.id) ? node : { ...node, position: undefined },
    ),
  };
};
